const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

export const SUPPORT_DURATION_MONTHS = Object.freeze({
  'پشتیبانی یک‌ماهه': 1,
  'پشتیبانی سه‌ماهه': 3,
  'پشتیبانی شش‌ماهه': 6,
  'پشتیبانی یک‌ساله': 12,
});

export const DEFAULT_SUPPORT_DURATION_OPTIONS = Object.freeze([
  Object.freeze({ months: 1, label: 'پشتیبانی یک‌ماهه', discountPercent: 0, badge: '', popular: false }),
  Object.freeze({ months: 3, label: 'پشتیبانی سه‌ماهه', discountPercent: 5, badge: '۵٪ تخفیف', popular: false }),
  Object.freeze({ months: 6, label: 'پشتیبانی شش‌ماهه', discountPercent: 10, badge: '۱۰٪ تخفیف', popular: true }),
  Object.freeze({ months: 12, label: 'پشتیبانی یک‌ساله', discountPercent: 20, badge: 'بهترین ارزش؛ ۲۰٪ تخفیف', popular: false }),
]);

const toEnglishDigits = (value) => String(value ?? '')
  .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
  .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)));

const numericPlanPrice = (value) => {
  const normalized = toEnglishDigits(value).replace(/[,٬،\s]/g, '');
  const match = normalized.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const formatPrice = (value) => Number(value).toLocaleString('fa-IR', {
  maximumFractionDigits: 2,
});

const priceInToman = (price, currency = '') => {
  const amount = numericPlanPrice(price);
  if (!Number.isFinite(amount)) return null;
  const unit = String(currency);
  if (/میلیون/.test(unit)) return Math.round(amount * 1_000_000);
  if (/هزار/.test(unit)) return Math.round(amount * 1_000);
  if (/ریال/.test(unit) && !/تومان/.test(unit)) return Math.round(amount / 10);
  return Math.round(amount);
};

const durationPricingOption = (durationOptions, months) => {
  const source = Array.isArray(durationOptions) && durationOptions.length
    ? durationOptions
    : DEFAULT_SUPPORT_DURATION_OPTIONS;
  return source.find((option) => Number(option?.months) === Number(months))
    || DEFAULT_SUPPORT_DURATION_OPTIONS.find((option) => option.months === Number(months))
    || null;
};

export const getServiceOrderPriceSummary = ({
  serviceSlug = '',
  groupId = '',
  durationOptions = null,
  plan = {},
  answers = {},
} = {}) => {
  const baseAmount = numericPlanPrice(plan.price);
  const hasExplicitMarketAmount = plan.marketAmount !== null
    && plan.marketAmount !== undefined
    && plan.marketAmount !== '';
  const explicitMarketAmount = Number(plan.marketAmount);
  const baseMarketAmount = hasExplicitMarketAmount && Number.isFinite(explicitMarketAmount) && explicitMarketAmount >= 0
    ? explicitMarketAmount
    : priceInToman(plan.price, plan.currency);
  const supportType = String(answers.support_type || '');
  const configuredDuration = (Array.isArray(durationOptions) ? durationOptions : [])
    .find((option) => String(option?.label || '') === supportType);
  const durationMonths = SUPPORT_DURATION_MONTHS[supportType]
    || Number(configuredDuration?.months)
    || null;
  const isWebsiteSupport = serviceSlug === 'website-support';
  const isRecurringSupport = isWebsiteSupport && groupId === 'monthly-support';
  const canCalculateTotal = isRecurringSupport && durationMonths && Number.isFinite(baseAmount);
  const durationOption = isRecurringSupport
    ? durationPricingOption(durationOptions, durationMonths)
    : null;
  const discountPercent = isRecurringSupport
    ? Math.max(0, Math.min(90, Number(durationOption?.discountPercent) || 0))
    : 0;
  const regularTotalAmount = canCalculateTotal ? baseAmount * durationMonths : baseAmount;
  const totalAmount = Number.isFinite(regularTotalAmount)
    ? regularTotalAmount * (1 - (discountPercent / 100))
    : regularTotalAmount;
  const regularMarketAmount = Number.isFinite(baseMarketAmount)
    ? baseMarketAmount * (isRecurringSupport ? (durationMonths || 0) : 1)
    : null;
  const totalMarketAmount = Number.isFinite(regularMarketAmount)
    ? Math.round(regularMarketAmount * (1 - (discountPercent / 100)))
    : null;
  const savingsMarketAmount = Number.isFinite(regularMarketAmount) && Number.isFinite(totalMarketAmount)
    ? Math.max(0, regularMarketAmount - totalMarketAmount)
    : 0;
  const effectiveMonthlyMarketAmount = isRecurringSupport && durationMonths && totalMarketAmount
    ? Math.round(totalMarketAmount / durationMonths)
    : baseMarketAmount;
  const effectiveMonthlyAmount = isRecurringSupport && durationMonths && Number.isFinite(totalAmount)
    ? totalAmount / durationMonths
    : baseAmount;

  return {
    baseAmount,
    totalAmount,
    regularTotalAmount,
    baseMarketAmount,
    regularMarketAmount,
    totalMarketAmount: totalMarketAmount > 0 ? totalMarketAmount : null,
    baseAmountToman: baseMarketAmount,
    regularTotalAmountToman: regularMarketAmount,
    totalAmountToman: totalMarketAmount > 0 ? totalMarketAmount : null,
    effectiveMonthlyAmount,
    effectiveMonthlyMarketAmount,
    savingsMarketAmount,
    effectiveMonthlyAmountToman: effectiveMonthlyMarketAmount,
    savingsAmountToman: savingsMarketAmount,
    discountPercent,
    durationBadge: durationOption?.badge || '',
    durationPopular: Boolean(durationOption?.popular),
    durationMonths,
    isWebsiteSupport,
    isRecurringSupport,
    isDurationAdjusted: Boolean(canCalculateTotal),
    displayAmount: Number.isFinite(totalAmount) ? formatPrice(totalAmount) : String(plan.price || ''),
    currency: plan.currency || '',
    periodLabel: isRecurringSupport && supportType ? supportType : plan.period || '',
    monthlyLabel: Number.isFinite(baseAmount)
      ? `${formatPrice(baseAmount)} ${plan.currency || ''} در ماه`
      : '',
    effectiveMonthlyLabel: Number.isFinite(effectiveMonthlyAmount)
      ? `${formatPrice(effectiveMonthlyAmount)} ${plan.currency || ''} در ماه`
      : '',
  };
};

export const getServicePaymentOptions = (priceSummary = {}, { paymentEnabled = true } = {}) => {
  if (!paymentEnabled || !priceSummary.isRecurringSupport || !(priceSummary.totalMarketAmount > 0)) {
    return [{ id: 'review_later', title: 'بررسی فنی و پرداخت بعداً', dueNow: 0 }];
  }

  const options = [{
    id: 'full',
    title: 'پرداخت کامل',
    description: 'کل مبلغ قرارداد را همین حالا پرداخت می‌کنم.',
    dueNow: priceSummary.totalMarketAmount,
    remaining: 0,
  }];

  const initialAmount = Math.ceil(priceSummary.totalMarketAmount / 2);
  options.push({
    id: 'two_stage',
    title: 'پرداخت اولیه',
    description: priceSummary.isRecurringSupport
      ? '۵۰٪ مبلغ به‌عنوان پرداخت اولیه و مانده در نیمه دوره قرارداد پرداخت می‌شود.'
      : '۵۰٪ مبلغ به‌عنوان پرداخت اولیه و مانده پیش از تحویل نهایی پرداخت می‌شود.',
    dueNow: initialAmount,
    remaining: priceSummary.totalMarketAmount - initialAmount,
    stageCount: 2,
  });

  return options;
};

export const formatToman = (amount) => Number(amount || 0).toLocaleString('fa-IR');
